import { FormEvent, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { projectsApi, timeEntriesApi } from '@/api/endpoints'
import type { Project, TimeEntry } from '@/api/types'
import { Button, Field, Input, Modal, Select, TimeInput } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { formatDuration } from '@/utils/format'

export default function EditEntryModal({
  entry,
  onClose,
  onSaved,
}: {
  entry: TimeEntry
  onClose: () => void
  onSaved: () => void
}) {
  const [projects, setProjects] = useState<Project[]>([])
  const [projectId, setProjectId] = useState(entry.projectId)
  const [description, setDescription] = useState(entry.description ?? '')
  const [date, setDate] = useState(dayjs(entry.startTime).format('YYYY-MM-DD'))
  const [from, setFrom] = useState(dayjs(entry.startTime).format('HH:mm'))
  const [to, setTo] = useState(entry.endTime ? dayjs(entry.endTime).format('HH:mm') : '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Archivované projekty nabízíme jen tehdy, když na jednom záznam už visí —
  // jinak by po archivaci nešel záznam uložit beze změny projektu.
  useEffect(() => {
    projectsApi
      .list({ includeArchived: true })
      .then((all) => setProjects(all.filter((p) => !p.archived || p.id === entry.projectId)))
  }, [entry.projectId])

  const start = useMemo(() => (from ? dayjs(`${date}T${from}`) : null), [date, from])
  const end = useMemo(() => (to ? dayjs(`${date}T${to}`) : null), [date, to])
  const durationSecs = start && end ? end.diff(start, 'second') : null

  const save = async (e: FormEvent) => {
    e.preventDefault()
    if (!start) return setError('Zadej platný začátek.')
    if (!entry.running && !end) return setError('Zadej platný konec.')
    if (durationSecs != null && durationSecs <= 0) return setError('Konec musí být po začátku.')

    setError(null)
    setSaving(true)
    try {
      await timeEntriesApi.update(entry.id, {
        projectId,
        description,
        startTime: start.toISOString(),
        // Běžícímu záznamu konec neposíláme, ať ho omylem nezastavíme.
        ...(end && !entry.running ? { endTime: end.toISOString() } : {}),
      })
      onSaved()
    } catch (err) {
      setError(errorMessage(err, 'Uložení se nepovedlo.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Upravit záznam" onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <Field label="Projekt">
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.clientName} · {p.name}
                {p.archived ? ' (archiv)' : ''}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Popis">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Co se dělalo?" />
        </Field>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-40 flex-1">
            <Field label="Datum">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
          </div>
          <div className="w-28">
            <Field label="Od">
              <TimeInput value={from} onChange={setFrom} />
            </Field>
          </div>
          <div className="w-28">
            <Field label="Do">
              {entry.running ? (
                <Input value="běží" disabled className="text-center text-slate-400" />
              ) : (
                <TimeInput value={to} onChange={setTo} />
              )}
            </Field>
          </div>
          <div className="pb-2 text-sm text-slate-500">
            {durationSecs != null && durationSecs > 0 ? formatDuration(durationSecs) : '—'}
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Zrušit
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Ukládám…' : 'Uložit'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
